import { useState } from "react";
import { useTranslation } from "react-i18next";

interface DateStepProps {
  selected: string;
  onSelect: (date: string) => void;
  onNext: () => void;
  onBack: () => void;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export default function DateStep({ selected, onSelect, onNext, onBack }: DateStepProps) {
  const { t } = useTranslation();
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isDisabled = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    return d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const formatDate = (day: number) => {
    const m = String(viewMonth + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${viewYear}-${m}-${d}`;
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C] mb-3" style={{ letterSpacing: "-0.02em" }}>
          {t("tabifoto.booking.select_date")}
        </h2>
        <div className="w-8 h-px bg-[#C8B8A6] mx-auto" />
      </div>

      {/* Calendar */}
      <div className="bg-white border border-[#D9D9D9] rounded-lg p-6 lg:p-8">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={prevMonth}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F7F6F3] cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line text-[#2C2C2C]/60" />
          </button>
          <span className="text-base font-light text-[#2C2C2C] tracking-widest">
            {monthNames[viewMonth]} {viewYear}
          </span>
          <button
            onClick={nextMonth}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F7F6F3] cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line text-[#2C2C2C]/60" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {dayNames.map((d) => (
            <div key={d} className="text-center text-xs text-[#2C2C2C]/30 uppercase tracking-wider py-2">
              {d}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells before first day */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = formatDate(day);
            const disabled = isDisabled(day);
            const isSelected = selected === dateStr;

            return (
              <button
                key={day}
                onClick={() => !disabled && onSelect(dateStr)}
                disabled={disabled}
                className={`aspect-square rounded-full text-sm font-light transition-all duration-150 cursor-pointer ${
                  isSelected
                    ? "bg-[#C8B8A6] text-white"
                    : disabled
                    ? "text-[#D9D9D9] cursor-not-allowed"
                    : "text-[#2C2C2C] hover:bg-[#C8B8A6]/15 hover:text-[#2C2C2C]"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <p className="text-center text-sm text-[#C8B8A6] mt-4 tracking-wide">
          {selected}
        </p>
      )}

      <div className="flex justify-between mt-10">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-full border border-[#D9D9D9] text-sm tracking-widest uppercase text-[#2C2C2C]/50 hover:border-[#2C2C2C] hover:text-[#2C2C2C] transition-all duration-200 whitespace-nowrap cursor-pointer"
        >
          ← {t("tabifoto.booking.back")}
        </button>
        <button
          onClick={onNext}
          disabled={!selected}
          className="px-8 py-3 rounded-full bg-[#2C2C2C] text-white text-sm tracking-widest uppercase hover:bg-[#2C2C2C]/80 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {t("tabifoto.booking.next")} →
        </button>
      </div>
    </div>
  );
}
